import type { ExecutionDashboardProjection } from '../../routes/execution/dashboardProjection';
import type { OperationalActionDescriptor } from '../../routes/execution/executionActionsFacade';
import type { ExecutionProgressHydration } from '../types';
import { isLoadingStop } from '../executionHelpers';
import type { StopOperationalContext } from '../../routes/execution/operationalContextFacade';
import { formatStopDestinationFromContext } from '../../routes/routeSummaryProjection';
import {
  buildCompletionReadiness,
  completionWarningsToStripItems
} from '../../operationalDomain/completion/completionReadiness';

export type DestinationAwareness = {
  stopLabel: string;
  address: string | null;
  clientName: string | null;
  consigneeName: string | null;
  applicationNumber: string | null;
  executionStopUid: string;
  kind: 'loading' | 'unloading';
};

export type SharedUnloadHint = {
  loadingStopCount: number;
  applicationCount: number;
  cargoCount: number;
  destinationLabel: string | null;
};

const QUICK_ACTION_RANK: Record<string, number> = {
  loaded: 10,
  start_loading: 10,
  complete_loading: 11,
  in_transit: 20,
  unloaded: 30,
  start_unloading: 30,
  complete_unloading: 31,
  partial_unloaded: 32
};

const QUICK_ACTION_LABELS: Partial<Record<string, string>> = {
  loaded: 'Погрузить',
  in_transit: 'Отправить',
  unloaded: 'Выгрузить',
  partial_unloaded: 'Частично',
  start_loading: 'Начать',
  complete_loading: 'Завершить',
  start_unloading: 'Начать',
  complete_unloading: 'Завершить'
};

/** Next operational destination — visible without popover (F2.4). */
export function buildDestinationAwareness(
  dashboard: ExecutionDashboardProjection
): DestinationAwareness | null {
  const { routeFlow, activeOperation, vehicleCargo } = dashboard;
  const stops = routeFlow.stops;
  if (stops.length === 0) return null;

  if (activeOperation) {
    const match = stops.find((s) => s.executionStopUid === activeOperation.executionStopUid);
    if (match && !match.isCompleted) {
      return toDestination(match);
    }
  }

  if (vehicleCargo.inVehiclePlaces > 0) {
    const nextUnload = stops.find((s) => s.type === 'unloading' && !s.isCompleted);
    if (nextUnload) return toDestination(nextUnload);
  }

  const nextIncomplete = stops.find((s) => !s.isCompleted);
  return nextIncomplete ? toDestination(nextIncomplete) : null;
}

function toDestination(
  stop: ExecutionDashboardProjection['routeFlow']['stops'][0]
): DestinationAwareness {
  return {
    stopLabel: stop.label,
    address: stop.address,
    clientName: stop.clientName,
    consigneeName: stop.consigneeName,
    applicationNumber: stop.applicationNumber,
    executionStopUid: stop.executionStopUid,
    kind: stop.type
  };
}

export function buildSharedUnloadHints(
  hydration: ExecutionProgressHydration,
  stopContextMap?: Map<string, StopOperationalContext>
): Map<string, SharedUnloadHint> {
  const map = new Map<string, SharedUnloadHint>();
  const unloadStops = hydration.stops.filter((s) => !isLoadingStop(s.type));

  for (const unload of unloadStops) {
    const incoming = hydration.edges.filter((e) => e.toExecutionStopUid === unload.executionStopUid);
    if (incoming.length <= 1) continue;

    const fromUids = new Set(incoming.map((e) => e.fromExecutionStopUid));
    const cargoKeys = new Set(incoming.map((e) => `${e.cargoId}:${e.fromExecutionStopUid}`));
    const ctx = stopContextMap?.get(unload.executionStopUid);

    map.set(unload.executionStopUid, {
      loadingStopCount: fromUids.size,
      applicationCount: fromUids.size,
      cargoCount: cargoKeys.size,
      destinationLabel: formatStopDestinationFromContext({
        pointType: unload.pointType,
        warehouseName: ctx?.warehouseName ?? unload.warehouseName,
        consigneeName: ctx?.consigneeName,
        applicationLabel: unload.pointType === 'application' ? ctx?.applicationLabel : null,
        clientName: ctx?.clientName,
        city: unload.city,
        address: unload.address
      })
    });
  }

  return map;
}

export function buildStopQuickActionMap(
  actions: OperationalActionDescriptor[]
): Map<string, OperationalActionDescriptor> {
  const map = new Map<string, OperationalActionDescriptor>();

  for (const action of actions) {
    if (!action.executionStopUid || action.disabled || action.opensIncident) continue;
    if (action.key === 'complete_route' || action.key === 'report_problem') continue;

    const uid = action.executionStopUid;
    const existing = map.get(uid);
    const rank = QUICK_ACTION_RANK[action.key] ?? 50;
    const existingRank = existing ? (QUICK_ACTION_RANK[existing.key] ?? 50) : 999;

    if (rank < existingRank) {
      map.set(uid, action);
    }
  }

  return map;
}

export function quickActionButtonLabel(descriptor: OperationalActionDescriptor): string {
  return QUICK_ACTION_LABELS[descriptor.key] ?? descriptor.label;
}

export type CompletionReadinessItem = {
  id: string;
  label: string;
  severity: 'warning' | 'info';
};

/** @deprecated Prefer dashboard.completionReadiness projection */
export function buildCompletionReadinessItems(params: {
  showCompletionHints: boolean;
  manualPenaltyCount: number;
  routePenaltyCount: number;
  unconfirmedExpenseCount: number;
  hasLogistCommentOnRoute: boolean;
}): CompletionReadinessItem[] {
  const readiness = buildCompletionReadiness({
    showCompletionHints: params.showCompletionHints,
    manualPenaltyCount: params.manualPenaltyCount,
    recordedPenaltyCount: params.routePenaltyCount,
    unconfirmedExpenseCount: params.unconfirmedExpenseCount,
    hasLogistCommentOnRoute: params.hasLogistCommentOnRoute
  });
  return completionWarningsToStripItems(readiness);
}
